import { useState } from "react";
import { useQueryCache, useMutation, MutationFunction, QueryKey } from "react-query";
import { useToasts } from "react-toast-notifications";
import { tryParseJSONObject } from "../features/admin/util/tryParseJSONObject";

interface IApiCallWithToast<TResult, TError = unknown, TVariables = undefined, TSnapshot = unknown> {
  apiCall: () => MutationFunction<TResult, TVariables>;
  onMutate?: (variables: TVariables) => TSnapshot | Promise<TSnapshot>; // function to run on start api call
  onSuccess?: (data: TResult, variables: TVariables) => void | Promise<unknown>; // function to run on successful response
  onFailure?: (error: TError, variables: TVariables, snapshot: TSnapshot) => void | Promise<unknown>; // function to run on failed response
  onSettled?: (
    data: TResult | undefined,
    error: TError | null,
    variables: TVariables,
    snapshotValue?: TSnapshot,
  ) => void | Promise<unknown>; // function to run on success or failure
  queryKey?: QueryKey; // runs invalidateQueries() if query key is provided
  shouldToastSuccess?: boolean; // determines whether or not to display success toast message
  shouldToastError?: boolean; // determines whether or not to display error toast message
  successMessage?: string;
  errorMessage?: string | ((error: TError) => string);
  useResponseErrorMessage?: boolean; // uses the error message from the response
}

// hook to allow for easier usage of API calls with toast messages
export default function useApiCallWithToast<TResult, TError = unknown, TVariables = undefined, TSnapshot = unknown>(
  props: IApiCallWithToast<TResult, TError, TVariables, TSnapshot>,
) {
  const {
    apiCall,
    onMutate,
    onSuccess = () => {},
    onFailure = () => {},
    onSettled = () => {},
    queryKey,
    shouldToastSuccess = true,
    shouldToastError = true,
    successMessage = "Changes saved successfully",
    errorMessage = "We ran into an issue saving your changes",
    useResponseErrorMessage = false,
  } = props;

  // We need separate state for this to capture the loading state of the mutation and the loading state of the query invalidation
  const [isLoading, setIsLoading] = useState(false);

  const { addToast } = useToasts();
  const queryCache = useQueryCache();
  const [mutation] = useMutation<TResult, TError, TVariables, TSnapshot>(apiCall(), {
    onSuccess: async (res, variables) => {
      if (queryKey) await queryCache.invalidateQueries(queryKey);
      if (shouldToastSuccess) addToast(successMessage, { appearance: "success", autoDismiss: true });
      onSuccess(res, variables);
      setIsLoading(false);
    },
    onError: (e, variables, snapshot) => {
      let errorString = errorMessage;
      if (typeof errorMessage === "function") errorString = errorMessage(e);
      if (useResponseErrorMessage) {
        errorString = (e as any).message ?? (e as any).error?.message;
        const errorMessageJSON = tryParseJSONObject(errorString);
        // handles ZOD error messages
        if (errorMessageJSON) {
          const errorMessageIsJSONArray = Array.isArray(errorMessageJSON);
          if (errorMessageIsJSONArray) {
            errorString = errorMessageJSON[0]?.message || errorString;
          }
        }
      }
      if (shouldToastError) addToast(errorString, { appearance: "error", autoDismiss: true });
      onFailure(e, variables, snapshot);
      setIsLoading(false);
    },
    onSettled: (data, e, variables, snapshotValue) => {
      onSettled(data, e, variables, snapshotValue);
      setIsLoading(false);
    },
    onMutate: onMutate,
  });

  // makes API call and displays toast message
  async function run(body?: TVariables) {
    setIsLoading(true);
    const res = await mutation(body);
    return res;
  }

  return { run, loading: isLoading };
}
